import { Box, Text } from 'ink';
import { useCallback, useMemo } from 'react';
import BuildTableLayout from '../../../components/BuildTableLayout';
import useBuildTableState from '../../../components/useBuildTableState';
import getRelativeDateFromDateString from '../../../utilities/getRelativeDateFromDateString';
import {
  getRunningBuildsWithIntrospection,
  BuildWithIntrospection,
} from '../utilities/buildQueries';

interface BuildDisplayItem {
  'ID': string;
  'Version': string;
  'Creation date': string;
  'Platforms': string;
  [key: string]: string;
}

export default function BuildSelector({
  configPath,
  onSelect,
}: {
  configPath?: string;
  onSelect: (buildId: string) => void;
}) {
  const load = useCallback(async () => {
    const builds = await getRunningBuildsWithIntrospection(configPath);

    if (builds.length === 0) {
      return { status: 'no-builds' } as const;
    }

    const displayBuilds = builds.map((build) => ({
      'ID': build.id,
      'Version': build.appVersion || 'unknown',
      'Creation date': getRelativeDateFromDateString(build.createdAt),
      'Platforms': getEnabledPlatforms(build),
    }));

    return { status: 'builds', builds: displayBuilds } as const;
  }, [configPath]);

  const { status, builds, error, selectItem } =
    useBuildTableState<BuildDisplayItem>({
      load,
    });

  const header = useMemo(
    () => (
      <Box marginBottom={1}>
        <Text>Select a build to introspect:</Text>
      </Box>
    ),
    [],
  );

  const footer = useMemo(() => {
    if (status !== 'show-builds') {
      return null;
    }

    return (
      <Box marginTop={1}>
        <Text dimColor>
          Showing {builds.length} running build
          {builds.length === 1 ? '' : 's'} with introspection enabled
        </Text>
      </Box>
    );
  }, [builds.length, status]);

  const noBuildsContent = useMemo(
    () => (
      <Box flexDirection="column">
        <Text color="yellow">No running builds with introspection found</Text>
        <Text dimColor>
          Start a build with --introspect flag to enable interactive debugging
        </Text>
      </Box>
    ),
    [],
  );

  if (status === 'selected') {
    return null;
  }

  function getEnabledPlatforms(build: BuildWithIntrospection): string {
    const platforms: string[] = [];

    if (build.introspect.mac?.enabled) platforms.push('mac');
    if (build.introspect.windows?.enabled) platforms.push('win');
    if (build.introspect.linux?.enabled) platforms.push('linux');

    return platforms.join(', ');
  }

  return (
    <BuildTableLayout
      builds={builds}
      commandUsed="todesktop introspect"
      error={error}
      footer={footer}
      header={header}
      loadingText="Loading builds with introspection enabled"
      noBuildsContent={noBuildsContent}
      onSelect={({ value }) => {
        selectItem(value.ID);
        onSelect(value.ID);
      }}
      status={status}
    />
  );
}
